<template>
  <div
    class="highlight-comment-section"
    :class="[
      `highlight-comment-section--${screenOrientationType}`,
      { 'highlight-comment-section--edit': isEditMode },
    ]"
  >
    <figure class="highlight-comment-section__image">
      <img :src="getCommentUserIconPath(displayCommentData.user.id)" alt="" />
    </figure>

    <div class="highlight-comment-section__content" v-if="!isEditMode">
      <div class="highlight-comment-section__header">
        <h6
          class="highlight-comment-section__header__title"
          v-text="getCommentUserDisplayName(displayCommentData.user.id)"
        />
        <comment-post-date-parts :postDate="displayCommentData.date" />
      </div>

      <p class="highlight-comment-section__comment" v-html="displayCommentData.comment" />

      <div class="highlight-comment-section__like">
        <like-button-parts :commentData="displayCommentData" @click="onChangeLike" />
      </div>

      <button class="highlight-comment-section__controller-toggle" @click="handleMenuClicked">
        <svg
          width="16"
          height="14"
          viewBox="0 0 16 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 6.05C1.86 6.05 1.34 6.57 1.34 7.2C1.34 7.84 1.86 8.36 2.5 8.36C3.13 8.36 3.65 7.84 3.65 7.2C3.65 6.57 3.13 6.05 2.5 6.05ZM7.9 6.05C7.26 6.05 6.74 6.57 6.74 7.2C6.74 7.84 7.26 8.36 7.9 8.36C8.53 8.36 9.05 7.84 9.05 7.2C9.05 6.57 8.53 6.05 7.9 6.05ZM13.3 6.05C12.66 6.05 12.14 6.57 12.14 7.2C12.14 7.84 12.66 8.36 13.3 8.36C13.93 8.36 14.45 7.84 14.45 7.2C14.45 6.57 13.93 6.05 13.3 6.05Z"
            fill="white"
          />
        </svg>
      </button>
    </div>
    <comment-editor-parts
      v-if="isEditMode"
      class="highlight-item-section__parent-comment__editor"
      :screenOrientationType="screenOrientationType"
      :message="displayCommentData.comment"
      @handlerCancel="enabledConfirmModal = true"
      @handlerSave="onSaveComment"
    />

    <!-- 入力キャンセル確認モーダル -->
    <confirm-modal-section
      class="highlight-comment-section__modal"
      v-if="enabledConfirmModal"
      :message="$tc('RaceVideoPage.highlights.comment.inputCancelModal.editCancelMessage')"
      :confirmButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.confirmButtonLabel')
      "
      :cancelButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.cancelButtonLabel')
      "
      @confirm="cancelInput"
      @cancel="enabledConfirmModal = false"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from '@vue/composition-api'
import CommentPostDateParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/CommentPostDateParts.vue'
import LikeButtonParts, {
  ClickedLikeDataType,
} from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/LikeButtonParts.vue'
import ConfirmModalSection from '@/components/common/modal/ConfirmModalSection.vue'
import { UserRetrieveNameResponseType } from '@/store/stores/collectionModule/documents/user/UserRetrieveNameDocument'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import useCommunication, { CommentDataType } from '@/components/RaceVideoPage/hook/useCommunication'
import CommentEditorParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/CommentEditorParts.vue'
import LoginStore from '@/store/stores/loginStore/LoginStore'

/* eslint-disable @typescript-eslint/no-var-requires */
const defaultIcon = require('@/assets/img/icon/icon_user_default.png')

/**
 * レース動画再生画面 ハイライト 投稿コメント セクションコンポーネント
 */
export default defineComponent({
  name: 'HighlightCommentSection',
  components: {
    CommentEditorParts,
    ConfirmModalSection,
    CommentPostDateParts,
    LikeButtonParts,
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 投稿コメントデータ
     */
    commentData: {
      type: Object as PropType<CommentDataType>,
      required: true,
    },
    showCommentInput: {
      type: Boolean,
      required: false,
    },
    loginUserId: {
      type: String,
      required: true,
    },
    /**
     * 対象ハイライトの編集状態
     */
    targetIsEditMode: {
      type: Boolean,
      default: false,
    },
    retrieveNameUsersByUserId: {
      type: Object as PropType<Record<string, UserRetrieveNameResponseType>>,
    },
  },
  setup(props, { emit }) {
    /** シェア、編集、削除ボタンを表示するかどうか */
    const controllerShown = ref<boolean>(false)
    /** ハイライトに対するコメント一覧 */
    const displayCommentData = computed((): CommentDataType => props.commentData)
    /** コメントシェア関数取得 */
    const { shareHighlightComment } = useCommunication()

    /**
     * enabledInput
     * @param {string} val ハイライトの入力状態
     */
    const isEditMode = computed(() => props.targetIsEditMode)

    // ログインユーザーコメント編集表示判定
    const isLoginUserComment = computed(
      () => props.loginUserId === displayCommentData.value.user.id && props.showCommentInput,
    )

    // コメントキャンセル確認用モーダル
    const enabledConfirmModal = ref<boolean>(false)

    /**
     * コメントしたユーザーの表示名を返す
     */
    const getCommentUserDisplayName = (userId: string) => {
      const targetUser = props.retrieveNameUsersByUserId?.[userId]
      return targetUser?.additionalData?.userDisplayName || '---'
    }
    /**
     * コメントしたユーザーのアイコン画像パスを返す
     */
    const getCommentUserIconPath = (userId: string) => {
      const targetUser = props.retrieveNameUsersByUserId?.[userId]
      return targetUser?.userPicture
        ? CloudFrontUtil.getSignedUrl(targetUser.userPicture)
        : defaultIcon
    }

    /**
     * コメントのメニューボタンが押下されたときの処理
     */
    const handleMenuClicked = () => {
      // 自分のコメントの場合は、編集、削除をできるようにする
      const slideMenu =
        LoginStore.value.loginId === displayCommentData.value.user.id
          ? 'highlightComment'
          : 'userBlock'

      emit('onClickMenu', displayCommentData.value, slideMenu)
    }

    return {
      controllerShown,
      displayCommentData,
      isLoginUserComment,
      isEditMode,
      enabledConfirmModal,
      getCommentUserDisplayName,
      getCommentUserIconPath,
      shareHighlightComment,
      handleMenuClicked,
    }
  },
  methods: {
    /**
     * コメント編集キャンセル
     */
    cancelInput() {
      this.enabledConfirmModal = false

      this.$emit('onToggleIsEditMode', false)
    },
    /**
     * コメント編集ボタンを押したことを親に伝える
     * @param comment string コメント
     */
    onSaveComment(comment: string) {
      // 入力状態でコメントがある場合
      this.$emit('handlerEdit', comment, this.displayCommentData.commentId)
    },
    /**
     * コメントLIKE処理
     * @param likeData 押下されたいいねの情報
     */
    onChangeLike(likeData: ClickedLikeDataType) {
      this.$emit('onChangeLike', likeData)
    },
  },
})
</script>

<style lang="scss" scoped>
.highlight-comment-section {
  position: relative;
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  color: $white;

  &--edit {
    padding: 10px 12px 0;
    gap: 6px;
    background: $black;
    border: 1px solid $black90;
    border-radius: 4px;
  }

  &__image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    overflow: hidden;
  }

  &__content {
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    justify-items: flex-start;
    align-content: flex-start;
    gap: 2px;
    width: calc(100% - 28px);
    padding: 10px 10px 6px;
    background: #2b2b2b;
    border-radius: 12px;
  }

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    font-family: $fontSignup;

    &__title {
      display: inline-block;
      max-width: calc(100% - 65px - 16px - 8px);
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      font-weight: 500;
      font-size: 10px;
      line-height: 1.4;
    }
  }

  &__comment {
    position: relative;
    width: 100%;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    letter-spacing: 0.02em;
    white-space: pre-wrap;
    overflow-wrap: break-word;
  }

  &__like {
    margin-top: 2px;
  }

  &__controller-toggle {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    width: 16px;
    height: 14px;
  }

  &--landscape-primary,
  &--landscape-secondary {
    .highlight-comment-section {
      &--edit {
        padding: 10px 10px 0;
      }

      &__header {
        &__title {
          max-width: calc(100% - 65px - 16px - 8px);
        }
      }

      &__controller {
        top: -40px;

        &__button {
          width: 66px;
          height: 34px;
          font-size: 12px;
        }
      }

      &__modal {
        left: auto;
        width: 230px;
      }
    }
  }
}
</style>

<style lang="scss">
.highlight-comment-section {
  &__modal {
    .modal__message {
      color: $font-color;
    }
  }
}
</style>
